import { Link } from "@ui/Atoms/Link/Link";
import { IconPlay } from "@ui/Icons/IconPlay";
import { SOCIAL_PLATFORM_ICON } from "@ui/Molecules/Social/SocialChips";
import { EmbedMedia } from "./EmbedMedia";
import type { EmbedCardData, EmbedCardVariant, EmbedPlatform } from "./types";

function PlatformBadge({ platform }: { platform: EmbedPlatform }) {
	const Icon = SOCIAL_PLATFORM_ICON[platform];
	return (
		<span className="absolute left-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(17_17_17_/_0.62)]">
			<Icon size={13} color="#fff" />
		</span>
	);
}

type Props = EmbedCardData & {
	variant?: EmbedCardVariant;
	/** Copy for the "no photo" media fallback. */
	emptyLabel: string;
	/** Eyebrow shown on the `feature` variant, e.g. "NIEUWSTE REEL". */
	eyebrow?: string;
};

function ViewsBadge({ views }: { views: string }) {
	return (
		<span className="absolute right-2.5 top-2.5 inline-flex items-center gap-1 rounded-[var(--radius-pill)] bg-[rgb(17_17_17_/_0.62)] px-2 py-1 text-[10.5px] font-bold text-white">
			<IconPlay size={9} color="#fff" />
			{views}
		</span>
	);
}

export function EmbedCard({
	name,
	href,
	area,
	cover,
	videoUrl,
	sourceUrl,
	platform,
	views,
	ago,
	label,
	variant = "grid",
	emptyLabel,
	eyebrow,
}: Props) {
	if (variant === "feature") {
		return (
			<Link
				href={href}
				className="relative block aspect-[9/16] overflow-hidden rounded-[var(--radius-lg)] bg-[var(--surface-inverted)] text-white"
			>
				<EmbedMedia
					cover={cover}
					videoUrl={videoUrl}
					sourceUrl={sourceUrl}
					platform={platform}
					name={name}
					emptyLabel={emptyLabel}
				/>
				<div className="absolute inset-x-0 bottom-0 h-1/2 bg-[linear-gradient(to_top,rgb(10_10_9_/_0.86),transparent)]" />
				{eyebrow && (
					<span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] bg-[rgb(255_255_255_/_0.92)] px-3 py-1.5 font-[family-name:var(--font-display)] text-[11px] font-extrabold tracking-[0.04em] text-[var(--content-primary)]">
						<span className="h-1.5 w-1.5 rounded-full bg-[var(--accent-500)]" />
						{eyebrow}
					</span>
				)}
				<div className="absolute inset-x-5 bottom-5 flex flex-col gap-1.5">
					<div className="font-[family-name:var(--font-display)] text-[30px] font-extrabold leading-[1.1] tracking-[-0.025em]">
						{name}
					</div>
					{(area || ago || views) && (
						<div className="text-[14px] text-[rgb(255_255_255_/_0.78)]">
							{[area, ago && `${ago} geleden`, views && `${views} views`]
								.filter(Boolean)
								.join(" · ")}
						</div>
					)}
				</div>
			</Link>
		);
	}

	if (variant === "rail") {
		return (
			<Link href={href} className="block w-[150px] shrink-0">
				<div className="relative aspect-[9/16] overflow-hidden rounded-[var(--radius-md)] border border-[var(--border-strong)] bg-[var(--map-land)]">
					<EmbedMedia
						cover={cover}
						videoUrl={videoUrl}
						name={name}
						emptyLabel={emptyLabel}
					/>
					<div className="absolute inset-x-0 bottom-0 h-[35%] bg-[linear-gradient(to_top,rgb(10_10_9_/_0.75),transparent)]" />
					{label ? (
						<span className="absolute left-2 top-2 rounded-[var(--radius-pill)] bg-[rgb(255_255_255_/_0.92)] px-2 py-1 font-[family-name:var(--font-display)] text-[10px] font-extrabold tracking-[0.03em] text-[var(--content-primary)]">
							{label}
						</span>
					) : (
						platform && <PlatformBadge platform={platform} />
					)}
					<div className="absolute inset-x-2.5 bottom-2 flex items-center justify-between text-[11px] font-bold text-white">
						{ago && <span>{ago}</span>}
						{views && (
							<span className="text-[rgb(255_255_255_/_0.8)]">{views}</span>
						)}
					</div>
				</div>
			</Link>
		);
	}

	return (
		<Link href={href} className="flex flex-col gap-2.5">
			<div className="relative aspect-[9/16] overflow-hidden rounded-[var(--radius-md)] border border-[var(--border-strong)] bg-[var(--map-land)]">
				<EmbedMedia
					cover={cover}
					videoUrl={videoUrl}
					name={name}
					emptyLabel={emptyLabel}
				/>
				<div className="absolute inset-x-0 bottom-0 h-[30%] bg-[linear-gradient(to_top,rgb(10_10_9_/_0.72),transparent)]" />
				{views && <ViewsBadge views={views} />}
				{ago && (
					<span className="absolute inset-x-3 bottom-2.5 text-[11px] font-bold text-[rgb(255_255_255_/_0.9)]">
						{ago}
					</span>
				)}
			</div>
			<div className="flex flex-col gap-0.5">
				<div className="truncate text-[15px] font-bold leading-[1.25] text-[var(--content-primary)]">
					{name}
				</div>
				{area && (
					<div className="truncate text-[13px] text-[var(--content-muted)]">
						{area}
					</div>
				)}
			</div>
		</Link>
	);
}
